import { useEffect } from "react";
import { Link } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { RelatedArticles } from "../../components/RelatedArticles";
import { TableOfContents } from "../../components/TableOfContents";
import { FloatingTableOfContents } from "../../components/FloatingTableOfContents";

const bestTemplates = [
  {
    brand: "Airbnb",
    slug: "booking-confirmation",
    title: "Booking Confirmation",
    category: "Transactional",
    why: "Clean layout, strong visual hierarchy, trust-building order details. The gold standard for transactional emails.",
  },
  {
    brand: "Stripe",
    slug: "payment-receipt",
    title: "Payment Receipt",
    category: "Invoice",
    why: "Minimal, data-dense receipt with clear line items. Shows how to handle financial information elegantly.",
  },
  {
    brand: "Netflix",
    slug: "welcome",
    title: "Welcome Email",
    category: "Onboarding",
    why: "High-impact hero image, single clear CTA, immediate value delivery. Textbook onboarding email.",
  },
  {
    brand: "Spotify",
    slug: "wrapped-summary",
    title: "Wrapped Summary",
    category: "Retention",
    why: "Data visualization in email format. Bold colors, shareable design. Viral retention done right.",
  },
  {
    brand: "Slack",
    slug: "channel-digest",
    title: "Channel Digest",
    category: "Newsletter",
    why: "Digest-style layout that handles dense information without overwhelming. Great for notification summaries.",
  },
  {
    brand: "Shopify",
    slug: "order-confirmation",
    title: "Order Confirmation",
    category: "Confirmation",
    why: "E-commerce transactional template with product details, shipping info, and clear next steps.",
  },
  {
    brand: "Notion",
    slug: "workspace-invite",
    title: "Workspace Invite",
    category: "Collaboration",
    why: "Collaborative email that drives immediate action. Clean invite card with one-tap acceptance.",
  },
  {
    brand: "Duolingo",
    slug: "streak-reminder",
    title: "Streak Reminder",
    category: "Retention",
    why: "Gamification in email form. Playful tone, urgent CTA, personality-driven retention at its best.",
  },
  {
    brand: "GitHub",
    slug: "pull-request-merged",
    title: "Pull Request Merged",
    category: "Alerts",
    why: "Developer notification template with code context, clean diff preview, and action links.",
  },
  {
    brand: "Amazon",
    slug: "shipping-confirmation",
    title: "Shipping Confirmation",
    category: "Transactional",
    why: "Tracking-heavy transactional email with delivery timeline, product image, and carrier details.",
  },
  {
    brand: "Docusign",
    slug: "signature-request",
    title: "Signature Request",
    category: "Transactional",
    why: "High-urgency, trust-focused template. Clear legal framing with single action CTA.",
  },
  {
    brand: "Tesla",
    slug: "service-appointment",
    title: "Service Appointment",
    category: "Confirmation",
    why: "Premium brand email with appointment details, location map, and calendar integration. Clean and authoritative.",
  },
];

const categoryGroups = [
  {
    name: "Onboarding & Welcome",
    description: "First impressions matter. These templates set the tone for your product relationship.",
    templates: [
      { brand: "Netflix", slug: "welcome", title: "Welcome" },
      { brand: "Airbnb", slug: "welcome", title: "Welcome" },
      { brand: "Spotify", slug: "welcome", title: "Welcome" },
      { brand: "Vercel", slug: "deployment-success", title: "Deployment Success" },
      { brand: "Notion", slug: "workspace-invite", title: "Workspace Invite" },
    ],
  },
  {
    name: "Transactional & Receipts",
    description: "Money moves. These handle payments, orders, and confirmations with clarity and trust.",
    templates: [
      { brand: "Stripe", slug: "payment-receipt", title: "Payment Receipt" },
      { brand: "Amazon", slug: "shipping-confirmation", title: "Shipping Confirmation" },
      { brand: "Shopify", slug: "order-confirmation", title: "Order Confirmation" },
      { brand: "Airbnb", slug: "booking-confirmation", title: "Booking Confirmation" },
      { brand: "PayPal", slug: "payment-confirmation", title: "Payment Confirmation" },
    ],
  },
  {
    name: "Retention & Re-engagement",
    description: "Bring users back. Streak reminders, wrap-ups, and win-back campaigns.",
    templates: [
      { brand: "Duolingo", slug: "streak-reminder", title: "Streak Reminder" },
      { brand: "Spotify", slug: "wrapped-summary", title: "Wrapped Summary" },
      { brand: "Netflix", slug: "new-release", title: "New Release" },
      { brand: "Grammarly", slug: "weekly-report", title: "Weekly Report" },
    ],
  },
  {
    name: "Security & Authentication",
    description: "High-trust emails for password resets, 2FA, and account alerts.",
    templates: [
      { brand: "Google", slug: "security-alert", title: "Security Alert" },
      { brand: "GitHub", slug: "login-alert", title: "Login Alert" },
      { brand: "Acme", slug: "password-reset", title: "Password Reset" },
    ],
  },
];

const blogTocItems = [
  { id: "top-12", text: "Top 12 templates you should start with", level: 2 },
  { id: "by-category", text: "Templates by category", level: 2 },
  { id: "why-react-email", text: "Why React Email templates?", level: 2 },
  { id: "how-to-use", text: "How to use these templates", level: 2 },
  { id: "common-email-types", text: "Most common email types", level: 2 },
];

export function BestReactEmailTemplates() {
  useEffect(() => {
    const cleanup = applyPageSeo({
      title: "Best React Email Templates for 2026 — 148 Free Examples by Brand",
      description:
        "Curated collection of the best React Email templates. 148 production-ready email templates inspired by Airbnb, Stripe, Netflix, Spotify, and 70+ brands. Free to preview; HTML export free, React source export on paid plans.",
      path: "/blog/best-react-email-templates",
      keywords: [
        "best react email templates",
        "react email templates",
        "email templates react",
        "react email components",
        "email template gallery",
        "brand email templates",
        "transactional email templates",
        "onboarding email template",
      ],
    });

    // Add Article structured data for Google rich results
    const articleJsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Best React Email Templates for 2026 — 148 Brand-Inspired Designs",
      "description": "Curated collection of the best React Email templates. 148 production-ready email templates inspired by Airbnb, Stripe, Netflix, Spotify, and 70+ brands.",
      "url": "https://reactemailbuilder.com/blog/best-react-email-templates",
      "datePublished": "2026-04-05",
      "dateModified": "2026-04-19",
      "author": { "@type": "Organization", "name": "React Email Builder" },
      "publisher": { "@type": "Organization", "name": "React Email Builder", "url": "https://reactemailbuilder.com" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(articleJsonLd);
    script.id = "article-jsonld";
    document.head.appendChild(script);

    return () => {
      cleanup();
      const existing = document.getElementById("article-jsonld");
      if (existing) existing.remove();
    };
  }, []);

  return (
    <div className="bg-white text-slate-950">
      {/* Hero */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white py-16 sm:py-20">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="mb-4 flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-400 transition hover:text-slate-600">Home</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <Link to="/blog" className="text-slate-400 transition hover:text-slate-600">Blog</Link>
            <svg aria-hidden="true" className="h-3.5 w-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            <span className="text-slate-700">Best React Email Templates</span>
          </nav>
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-700">
            Template Collection
          </p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            The Best React Email Templates for 2026
          </h1>
          <p className="mt-6 text-lg leading-8 text-slate-600 sm:text-xl">
            148 production-ready email templates inspired by the world's best product emails.
            Every template is a real, renderable React Email component — not a screenshot.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/discover/templates"
              className="inline-flex items-center gap-2 rounded bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Browse all 148 templates
            </Link>
            <Link
              to="/builder"
              className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
            >
              Build your own with AI
            </Link>
          </div>

          {/* Mobile TOC */}
          <div className="mt-8 xl:hidden">
            <TableOfContents items={blogTocItems} />
          </div>
        </div>
      </section>

      <FloatingTableOfContents items={blogTocItems} />

      {/* Top 12 */}
      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1fr_260px]">
            <div>
              <h2 id="top-12" className="text-3xl font-bold tracking-tight sm:text-4xl">
                Top 12 templates you should start with
              </h2>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            These templates represent the highest quality in our library.
            Each one demonstrates best practices for layout, hierarchy, and brand consistency.
          </p>

              <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {bestTemplates.map((t) => (
                  <Link
                    key={`${t.brand}-${t.slug}`}
                    to={`/templates/${t.brand.toLowerCase()}/${t.slug}`}
                    className="group rounded-md border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-950 text-sm font-bold text-white">
                        {t.brand.charAt(0)}
                      </span>
                      <div>
                        <h3 className="font-semibold text-slate-950 group-hover:text-sky-700">
                          {t.brand} {t.title}
                        </h3>
                        <span className="text-xs font-medium text-slate-500">{t.category}</span>
                      </div>
                    </div>
                    <p className="mt-4 text-sm leading-6 text-slate-600">{t.why}</p>
                  </Link>
                ))}
              </div>
            </div>
            <aside className="hidden lg:block xl:hidden">
              <div className="sticky top-24">
                <TableOfContents items={blogTocItems} />
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Mid-content CTA */}
      <section className="border-y border-slate-200 bg-slate-50 py-8">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 rounded-md border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:px-8">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                Need a custom email template?
              </p>
              <p className="mt-1 text-sm text-slate-600">
                Describe it in plain English. Our AI generates production-ready React Email code in seconds.
              </p>
            </div>
            <Link
              to="/auth/signup?redirect=%2Fbuilder"
              className="shrink-0 rounded bg-slate-950 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
            >
              Try AI builder free
            </Link>
          </div>
        </div>
      </section>

      {/* By Category */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 id="by-category" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Templates by category
          </h2>
          <p className="mt-4 max-w-2xl text-lg text-slate-600">
            Whatever email you need to send, there's a template that already solved the layout problem.
          </p>

          <div className="mt-10 space-y-12">
            {categoryGroups.map((group) => (
              <div key={group.name}>
                <h3 className="text-xl font-semibold text-slate-950">{group.name}</h3>
                <p className="mt-2 text-base text-slate-600">{group.description}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {group.templates.map((t) => (
                    <Link
                      key={`${t.brand}-${t.slug}`}
                      to={`/templates/${t.brand.toLowerCase()}/${t.slug}`}
                      className="inline-flex items-center gap-2 rounded border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-slate-950 text-[10px] font-bold text-white">
                        {t.brand.charAt(0)}
                      </span>
                      {t.brand} — {t.title}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why React Email */}
      <section className="border-t border-slate-200 bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="why-react-email" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Why React Email templates?
          </h2>
          <div className="mt-8 space-y-6 text-lg leading-8 text-slate-600">
            <p>
              React Email has become the standard for building transactional and product emails in modern
              web applications. Unlike traditional HTML email templates — which are fragile, hard to maintain,
              and impossible to unit test — React Email components are:
            </p>
            <ul className="ml-6 list-disc space-y-3">
              <li><strong className="text-slate-950">Type-safe</strong> — TypeScript catches errors before you send. No more typos in prop names or missing required fields.</li>
              <li><strong className="text-slate-950">Component-based</strong> — Build a <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">&lt;Header /&gt;</code>, <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">&lt;Footer /&gt;</code>, or <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">&lt;OrderSummary /&gt;</code> once and reuse it across every template.</li>
              <li><strong className="text-slate-950">Testable</strong> — Unit test your email templates like any other React component with Jest, Vitest, or Testing Library.</li>
              <li><strong className="text-slate-950">Versionable</strong> — Git-track your email code alongside your product code. Review changes in pull requests. Roll back when needed.</li>
              <li><strong className="text-slate-950">Portable</strong> — Render to HTML and send through any ESP — Resend, SendGrid, Mailchimp, Postmark, AWS SES, or any provider that accepts HTML.</li>
            </ul>
            <p>
              Every template in our gallery is a real React Email component using <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">@react-email/components</code>.
              You can export the source code and drop it directly into your project — no conversion, no copy-pasting
              raw HTML, no "works in Gmail but breaks in Outlook" surprises.
            </p>
          </div>
        </div>
      </section>

      {/* How to use these templates */}
      <section className="border-t border-slate-200 bg-slate-50 py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="how-to-use" className="text-3xl font-bold tracking-tight sm:text-4xl">
            How to use these templates in your project
          </h2>
          <div className="mt-8 space-y-6 text-lg leading-8 text-slate-600">
            <p>
              Getting started with React Email templates is straightforward. Whether you're building transactional
              emails for a SaaS product, order confirmations for an e-commerce store, or onboarding sequences for
              a mobile app, the workflow is the same:
            </p>
            <ol className="ml-6 list-decimal space-y-4">
              <li>
                <strong className="text-slate-950">Browse the gallery.</strong> Find a template that matches your use case —
                filter by category (transactional, onboarding, marketing, retention) or brand inspiration.
                Each template is a complete, renderable email — not a wireframe or screenshot.
              </li>
              <li>
                <strong className="text-slate-950">Preview it live.</strong> Click any template to see the rendered HTML preview.
                Toggle between desktop and mobile views to check responsive behaviour. Every template is built
                with <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">@react-email/components</code>,
                so it renders consistently across Gmail, Outlook, Apple Mail, Yahoo, and other major clients.
              </li>
              <li>
                <strong className="text-slate-950">Export as HTML or React source.</strong> Download the rendered HTML for immediate
                use in any email service provider, or export the React Email <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">.tsx</code>
                source code to integrate into your own codebase. HTML export is free — no account needed.
              </li>
              <li>
                <strong className="text-slate-950">Customise with AI or code.</strong> Use the AI builder to modify any template
                with a plain-language prompt ("Make the CTA blue and add a discount code section"), or edit
                the JSX directly since it's just React code. The free tier includes 3 AI generations per day.
              </li>
              <li>
                <strong className="text-slate-950">Integrate with your ESP.</strong> Send the rendered HTML through Resend, SendGrid,
                AWS SES, Mailchimp, Postmark, or any provider that accepts HTML email. If you're using React Email
                components in your codebase, render server-side with <code className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800">@react-email/render</code> and
                pass the result to your email API.
              </li>
            </ol>
          </div>
        </div>
      </section>

      {/* Common email types */}
      <section className="border-t border-slate-200 bg-white py-14 sm:py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="common-email-types" className="text-3xl font-bold tracking-tight sm:text-4xl">
            Most common email types (and which templates work best)
          </h2>
          <div className="mt-8 space-y-8 text-lg leading-8 text-slate-600">
            <div>
              <h3 className="text-xl font-semibold text-slate-950">Welcome &amp; onboarding emails</h3>
              <p className="mt-2">
                Your welcome email sets the tone for the entire user relationship. The best onboarding emails
                have a single clear CTA, minimal text, and an immediate sense of value. Our Netflix welcome
                template is a textbook example: hero image, one action button, done. The Airbnb welcome takes
                a different approach — highlighting 3 key features with distinct CTAs for each.
              </p>
              <p className="mt-2">
                What to look for in a welcome template: a strong hero section, a single primary CTA, social proof
                or feature highlights, and a clean footer with useful links. Avoid walls of text — new users
                spend an average of 8 seconds reading a welcome email.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-950">Transactionals: receipts, confirmations, and shipping updates</h3>
              <p className="mt-2">
                Transactional emails have the highest open rates of any email category (60–70% according to
                Mailchimp data). That makes them your most visible brand touchpoint — yet most teams treat them
                as an afterthought. Our Stripe payment receipt template demonstrates the gold standard: minimal,
                data-dense, scannable. The Amazon shipping confirmation adds tracking timelines and carrier
                details — essential for e-commerce.
              </p>
              <p className="mt-2">
                Key elements of a good transactional email: clear subject line, order/reference number prominently
                displayed, a single actionable link (track, view receipt, manage booking), and trust signals
                (brand colours, professional layout, no spammy content).
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-950">Retention &amp; re-engagement</h3>
              <p className="mt-2">
                Bringing users back is harder than acquiring them. Duolingo's streak reminder is the masterclass
                — gamification, urgency, and personality all in one compact email. Spotify's Wrapped turns
                retention content into a shareable event. These templates show how to use data visualization,
                personal stats, and milestone celebration to drive engagement.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-950">Security &amp; authentication</h3>
              <p className="mt-2">
                Password reset emails, 2FA codes, and login alerts need to be functional above all else.
                But they're also a trust signal — a poorly designed security email undermines confidence.
                Our Google security alert and GitHub login alert templates show how to keep these emails
                clear, actionable, and reassuring without overdesigning.
              </p>
              <p className="mt-2">
                Best practices: single CTA button (never a raw link), expiration notice, "if you didn't
                request this" disclaimer, and sender verification (show the user's email or account name).
              </p>
            </div>
          </div>
        </div>
      </section>

      <RelatedArticles exclude="/blog/best-react-email-templates" />

      {/* CTA */}
      <section className="border-t border-slate-200 bg-slate-950 py-14 text-white sm:py-16">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Find the perfect email template
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-300">
            148 templates. 75 brands. Free to preview. Export as HTML or React source.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <Link
              to="/discover/templates"
              className="inline-flex items-center gap-2 rounded bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
            >
              Browse templates
            </Link>
            <Link
              to="/auth/signup?redirect=%2Fbuilder"
              className="inline-flex items-center gap-2 rounded border border-white/40 bg-white/10 px-6 py-3 text-sm font-semibold text-white transition hover:border-white/60 hover:bg-white/15"
            >
              Start building free
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}